/**
 * Display Settings Popover
 *
 * Quick-access popover for display/visual accessibility settings:
 * - Theme (Dark/Light Mode)
 * - High Contrast
 * - Reduce Motion
 * - Screen Reader Mode
 */

import { useState, useEffect } from 'react'
import { Monitor, RotateCcw, Sun, Moon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useTheme } from '@/contexts/ThemeContext'
import {
  getUserAccessibilitySettings,
  saveUserAccessibilitySettings,
  applyUserAccessibilitySettings,
  type UserAccessibilitySettings,
} from '@lina/accessibility/user'

interface DisplaySettingsPopoverProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
}

export function DisplaySettingsPopover({
  open,
  onOpenChange,
  children,
}: DisplaySettingsPopoverProps) {
  const [settings, setSettings] = useState<UserAccessibilitySettings>(() =>
    getUserAccessibilitySettings(),
  )
  const { theme, setTheme } = useTheme()

  // Update DOM when settings change
  useEffect(() => {
    applyUserAccessibilitySettings(settings)
    saveUserAccessibilitySettings(settings)
  }, [settings])

  const updateSetting = <K extends keyof UserAccessibilitySettings>(
    key: K,
    value: UserAccessibilitySettings[K],
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const handleReset = () => {
    const defaults = {
      highContrast: false,
      reduceMotion: false,
      screenReaderOptimized: false,
    }
    setSettings((prev) => ({ ...prev, ...defaults }))
    setTheme('light')
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {children || (
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Display settings">
            <Monitor className="w-4 h-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80" align="center" side="top">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h4 className="text-sm font-semibold">Display Settings</h4>
              <p className="text-xs text-muted-foreground">Customize visual appearance</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleReset}
              title="Reset display settings"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>

          <div className="border-t pt-3 space-y-3">
            {/* Theme Toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Theme</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="h-8 gap-2"
              >
                {theme === 'dark' ? (
                  <>
                    <Moon className="w-3 h-3" />
                    <span className="text-xs">Dark</span>
                  </>
                ) : (
                  <>
                    <Sun className="w-3 h-3" />
                    <span className="text-xs">Light</span>
                  </>
                )}
              </Button>
            </div>

            {/* High Contrast */}
            <div className="flex items-center justify-between">
              <Label htmlFor="display-contrast" className="text-xs font-medium cursor-pointer">
                High Contrast
              </Label>
              <Switch
                id="display-contrast"
                checked={settings.highContrast}
                onCheckedChange={(checked) => updateSetting('highContrast', checked)}
              />
            </div>

            {/* Reduce Motion */}
            <div className="flex items-center justify-between">
              <Label htmlFor="display-motion" className="text-xs font-medium cursor-pointer">
                Reduce Motion
              </Label>
              <Switch
                id="display-motion"
                checked={settings.reduceMotion}
                onCheckedChange={(checked) => updateSetting('reduceMotion', checked)}
              />
            </div>

            {/* Screen Reader Mode */}
            <div className="flex items-center justify-between">
              <Label htmlFor="display-screen-reader" className="text-xs font-medium cursor-pointer">
                Screen Reader Mode
              </Label>
              <Switch
                id="display-screen-reader"
                checked={settings.screenReaderOptimized}
                onCheckedChange={(checked) => updateSetting('screenReaderOptimized', checked)}
              />
            </div>
          </div>

          {/* Info */}
          <div className="text-xs text-muted-foreground border-t pt-2">
            💡 Settings save automatically
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
